import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { IoEvent, Project, ProjectEvent } from '../peregrineFlowInterfaces';
import { socket } from '../socket';
import { styled } from '@mui/material/styles';

import IconButton from '@mui/material/IconButton';
import { FileOpenOutlined, NoteAddOutlined, DeleteOutline, Share } from '@mui/icons-material';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import { Alert, Button, Container, DialogActions, InputAdornment, Paper, Popover, TextField, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { enqueueSnackbar } from 'notistack';

export type OnSelectProject = (project: Project) => void;

type ProjectListProps = {
    onSelectProject?: OnSelectProject
    activeProject?: string
    isGuest?: boolean
}

const BackgroundPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(2)
}));

const ProjectList = ({ onSelectProject, activeProject, isGuest = false }: ProjectListProps) => {
    const [isLoading, setIsLoading] = useState(true);
    const [projectList, setProjectList] = useState<Array<any>>([]);
    const [newProjectName, setNewProjectName] = useState<string>('');
    const [confirmAnchorEl, setConfirmAnchorEl] = useState<HTMLButtonElement | null>(null);
    const [activeConfirmId, setActiveConfirmId] = useState<string | undefined>(undefined);
    const [query, setQuery] = useState("");
    const openConfirm = Boolean(confirmAnchorEl);

    useEffect(() => {
        const onListProjects = (data: IoEvent) => {
            if (data.event_type !== 'list_projects')
                return;

            setIsLoading(false);

            if (!data.data || !Array.isArray(data.data)) {
                setProjectList([]);
                return;
            }
            setProjectList(data.data as Array<any>);
        };

        socket.on('io', onListProjects);

        // Fetch projects - this runs on mount and when isGuest changes (e.g., after login)
        setIsLoading(true);
        const event = { event_type: 'list_projects' } as IoEvent;
        socket.emit('io', event);

        return () => {
            socket.off('io', onListProjects);
        };
    }, [isGuest]); // Re-fetch when guest status changes (e.g., after login)

    useEffect(() => {
        const onProject = (data: ProjectEvent) => {
            if (data.event_type === 'read' || data.event_type === 'add') {
                onSelectProject?.(data.project as Project);
                setIsLoading(true);
                setQuery("")
            } else if (data.event_type === 'delete') {
                // Re-fetch project list after deletion
                setIsLoading(true);
                const event = { event_type: 'list_projects' } as IoEvent;
                socket.emit('io', event);
            }

        };
        socket.on('project', onProject);
        return () => {
            socket.off('project', onProject);
        };
    }, [onSelectProject, setIsLoading, setQuery]);

    const sortedProjectList = useMemo(() => {
        const sort_fn = (a: any, b: any) => (a.path.toLowerCase() > b.path.toLowerCase()) ? 1 : -1;
        const ownProjects = projectList.filter((p) => !p.path.includes('templates')).sort(sort_fn);
        const templatesProjects = projectList.filter((p) => p.path.includes('templates')).sort(sort_fn);


        return [templatesProjects, ownProjects]
    }, [projectList])


    const selectProject = useCallback((project: any) => {
        const event = { event_type: 'read', project: project.path } as ProjectEvent;
        socket.emit('project', event);
    }, []);

    const createProject = useCallback(() => {
        if (newProjectName === '') {
            return;
        }

        const event = { event_type: 'add', project: newProjectName } as ProjectEvent;
        socket.emit('project', event);

    }, [newProjectName]);

    const deleteProject = useCallback((project: any) => {
        const event = { event_type: 'delete', project: project.path } as ProjectEvent;
        socket.emit('project', event);

        setConfirmAnchorEl(null);
        setIsLoading(true);
    }, []);

    const shareTemplate = useCallback((project: any) => {
        // Extract template name without .flow extension
        const templateName = project.name.replace('.flow', '');
        const templateUrl = `${window.location.origin}/templates/${templateName}`;

        // Copy to clipboard
        navigator.clipboard.writeText(templateUrl).then(() => {
            enqueueSnackbar('Template URL copied to clipboard!', { variant: 'success' });
        }).catch(() => {
            enqueueSnackbar('Failed to copy URL', { variant: 'error' });
        });
    }, []);


    const newProjectContent = useCallback(() => {
        // Guests cannot create projects
        if (isGuest) {
            return null;
        }

        return (
            <BackgroundPaper sx={{ marginBottom: 2 }}>
                <Typography variant='h5' sx={{ mb: 2 }}>Create new project</Typography>
                <Box sx={{ m: 1 }}>
                    <TextField size='small'
                        id="new-project-name-field" label="New project name" variant="outlined"
                        onChange={(e) => setNewProjectName(e.target.value)}
                        value={newProjectName} sx={{ mr: 2 }}
                    />
                    <Button size='medium' variant="outlined" onClick={() => createProject()}><NoteAddOutlined />Create</Button>
                </Box>
            </BackgroundPaper>
        )
    }, [newProjectName, createProject, isGuest]);

    const listItem = useCallback((project: any, index: number, isTemplate: boolean) => {
        if (query !== '' && !project.path.toLowerCase().includes(query.toLowerCase())) {
            return null;
        }

        // Guests cannot delete projects
        const canDelete = !isGuest && !isTemplate && activeProject !== project.name;

        return (
            <ListItem key={project.name} divider={index < projectList.length - 1} secondaryAction={
                isTemplate ? (
                    <IconButton
                        edge="end"
                        aria-label="share"
                        onClick={() => shareTemplate(project)}>
                        <Share />
                    </IconButton>
                ) : (
                    !isGuest && (
                        <IconButton
                            aria-describedby={openConfirm ? `confirm-${project.name}` : undefined}
                            edge="end"
                            aria-label="delete"
                            disabled={!canDelete}
                            onClick={(e) => { setConfirmAnchorEl(e.currentTarget); setActiveConfirmId(`confirm-${project.name}`) }}>
                            <DeleteOutline />
                        </IconButton>
                    )
                )
            }>
                <ListItemButton onClick={() => selectProject(project)}>
                    <ListItemIcon>
                        <FileOpenOutlined />
                    </ListItemIcon>
                    <ListItemText
                        primary={project.name}
                        secondary={`Modified: ${project.modified_date}`}
                    />
                </ListItemButton>
                <Popover
                    id={openConfirm ? `confirm-${project.name}` : undefined}
                    key={`confirm-${project.name}`}
                    open={activeConfirmId === `confirm-${project.name}`}
                    anchorEl={confirmAnchorEl}
                    onClose={() => { setConfirmAnchorEl(null); setActiveConfirmId(undefined); }}
                    anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                    }}
                >
                    <Typography sx={{ p: 2 }}>Are you sure you want to delete the file "{project.name}" ?</Typography>
                    <DialogActions>
                        <Button autoFocus onClick={() => { setConfirmAnchorEl(null); setActiveConfirmId(undefined) }}>No</Button>
                        <Button onClick={() => deleteProject(project)}>Yes</Button>
                    </DialogActions>
                </Popover>
            </ListItem>
        )
    }, [openConfirm, confirmAnchorEl, query, activeConfirmId, activeProject, deleteProject, selectProject, projectList.length, isGuest, shareTemplate]);

    const templatesContent = useCallback(() => {
        if (isLoading) {
            return (
                <BackgroundPaper sx={{ display: 'flex', justifyContent: 'center', p: 4, marginBottom: 2 }}>
                    <CircularProgress />
                </BackgroundPaper>
            )
        }

        return (
            <BackgroundPaper sx={{ marginBottom: 2 }}>
                <Typography variant='h5'>Templates</Typography>
                {sortedProjectList[0].length === 0 ? (
                    <Alert severity='info'>No templates found</Alert>
                ) : (
                    <List sx={{ width: '100%', bgcolor: 'background.paper' }} key="templates">
                        {sortedProjectList[0].map((project, projectIndex) => (
                            listItem(project, projectIndex, true)
                        ))}
                    </List>
                )}
            </BackgroundPaper>
        )

    }, [isLoading, listItem, sortedProjectList]);


    const ownProjectsContent = useCallback(() => {
        if (isLoading) {
            return (
                <BackgroundPaper sx={{ display: 'flex', justifyContent: 'center', p: 4, marginBottom: 2 }}>
                    <CircularProgress />
                </BackgroundPaper>
            )
        }

        return (
            <BackgroundPaper sx={{ marginBottom: 2 }}>
                <Typography variant='h5'>Projects</Typography>
                {sortedProjectList[1].length === 0 ? (
                    <Alert severity='info'>No existing projects found</Alert>
                ) : (
                    <List sx={{ width: '100%', bgcolor: 'background.paper' }} key="own-projects">
                        {sortedProjectList[1].map((project, projectIndex) => (
                            listItem(project, projectIndex, false)
                        ))}
                    </List>
                )}
            </BackgroundPaper>
        )

    }, [isLoading, listItem, sortedProjectList]);

    return (
        <Container maxWidth="md" sx={{ py: 4, flex: 1, overflow: 'auto' }}>
            <TextField
                fullWidth
                size="small"
                placeholder="Search projects..."
                value={query}
                onChange={event => setQuery(event.target.value.toLowerCase())}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon color="action" />
                        </InputAdornment>
                    )
                }}
                sx={{ mb: 3 }}
            />
            {newProjectContent()}
            {templatesContent()}
            {ownProjectsContent()}
        </Container>
    );
};

export default ProjectList;
